import {
  DatePicker,
  DateTimePicker,
  Dropdown,
  Switch,
  TextField,
} from "@flat-ui";
import { Grid, Stack } from "@mui-wrapper";
import { DatePickerLabel } from "@pages/create-plan/create-plan-components/date-picker-label";
import { PlanRangeInfo } from "@pages/create-plan/create-plan-container/plan-range-info";
import { PlanCreationModeProps } from "@custom-types/base-types";
import { DateTimeUtils } from "@stellar/utils";
import { devFeaturesStateSelector } from "@store/dev-features/dev-features-slice";
import {
  CopyDurationMode,
  plansCreationStateSelector,
  setCopyDurationMode,
  setCreationDates,
  setPlanEndDate,
  setShouldEndCopiedPlan,
} from "@store/plan-creation/plan-creation-slice";
import {
  doesCopyOverlapCopiedPlanSelector,
  isEndingCopiedPlanSelector,
} from "@store/plan-creation/plan-creation-selectors";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  PLAN_DURATION_LABELS,
  PLAN_DURATION_TYPES,
  PlanDurationType,
  validatePlanDuration,
} from "@utils/plan-duration";
import { useIsDarkMode } from "@hooks/use-is-dark-mode";
import { useMemo } from "react";

/**
 * Styles to align a flat-ui date picker with the flat-ui TextField next to it.
 *
 * The text field renders at MUI's default outlined-input height, while the date
 * picker declares 38px on its own field, so the picker field is matched to the
 * text field explicitly.
 */
const DATE_FIELD_ALIGNMENT_SX = {
  "& .MuiPickersTextField-root": {
    "& .MuiPickersInputBase-root": { height: "56px", fontSize: "1rem" },
  },
} as const;

/** The ways the end date of a copy can be set, in the order they are offered to the user */
const COPY_DURATION_MODE_OPTIONS: Array<{
  value: CopyDurationMode;
  label: string;
  secondaryText: string;
}> = [
  {
    value: "newSubscription",
    label: "Start new subscription",
    secondaryText: "Select the duration, the end date is calculated from it",
  },
  {
    value: "remainingTime",
    label: "Use remaining time",
    secondaryText:
      "The copy runs from its start date until the copied plan ends",
  },
];

/**
 * Selecting the duration of the plan, from which its end date is derived, or the other way round
 * while the full date and time dev tool is on
 */
export function CreatePlanDateSection({
  creationMode,
}: PlanCreationModeProps): React.JSX.Element {
  const {
    creationDates: { startDate, endDate, isFixed, durationType, durationValue },
    copiedPlan,
  } = useAppSelector(plansCreationStateSelector);
  const { shouldShowFullDateTime } = useAppSelector(devFeaturesStateSelector);
  const isCopyOverlapping = useAppSelector(doesCopyOverlapCopiedPlanSelector);
  const shouldEndCopiedPlan = useAppSelector(isEndingCopiedPlanSelector);

  const dispatch = useAppDispatch();
  // The date pickers do not follow the theme, they are given the variant of the colour scheme
  const datePickerVariant = useIsDarkMode() ? "Dark" : "Light";

  const isUnlimited = durationType === "unlimited";

  // Reversing the direction lets a plan be made to expire within the next minutes. The end date of
  // a copy always follows from its duration, so it cannot be set on its own.
  const shouldDeriveDurationFromDates =
    shouldShowFullDateTime && !isFixed && !isUnlimited && copiedPlan === null;

  // Only single features and bundles can be granted forever, a paid plan always expires
  const durationOptions = useMemo(
    () =>
      PLAN_DURATION_TYPES.filter(
        (type) => type !== "unlimited" || creationMode === "feature"
      ).map((type) => ({
        key: type,
        value: type,
        label: PLAN_DURATION_LABELS[type].option,
      })),
    [creationMode]
  );

  function onDurationTypeChange(newDurationType: PlanDurationType): void {
    dispatch(
      setCreationDates({
        durationType: newDurationType,
        // An unlimited plan hides its date pickers, so it always starts on the current day
        ...(newDurationType === "unlimited" ? { startDate: Date.now() } : {}),
      })
    );
  }

  const DateComponent = shouldShowFullDateTime ? DateTimePicker : DatePicker;

  const durationMode = copiedPlan?.durationMode;

  // The start date of a copy can be moved whatever the product dictates
  const isStartDateFixed = copiedPlan === null && isFixed === true;

  // A copy using the remaining time ends with the copied plan, so it has to start before that
  const maxStartDate =
    durationMode === "remainingTime" && copiedPlan?.remainingDates
      ? new Date(copiedPlan.remainingDates.endDate)
      : undefined;

  /** Explains how the start date of a copy relates to the copied plan */
  function getCopyStartDateTooltip(): string | undefined {
    if (durationMode === "remainingTime") {
      return shouldEndCopiedPlan
        ? "The copied plan ends when the copy starts, and the copy runs until the copied plan would have ended"
        : "The copy runs until the copied plan ends";
    }

    return shouldEndCopiedPlan
      ? "The copied plan ends when the copy starts"
      : undefined;
  }

  // The remaining time follows from the start date and the end of the copied plan
  const isDurationFixed =
    isFixed === true ||
    durationMode === "remainingTime" ||
    shouldDeriveDurationFromDates;

  const copyDurationModeOptions = COPY_DURATION_MODE_OPTIONS.map((option) => ({
    ...option,
    key: option.value,
    isDisabled:
      option.value === "remainingTime" && copiedPlan?.remainingDates === null,
  }));

  return (
    <Grid size={{ md: 6 }}>
      <Grid container spacing={1} sx={{ alignItems: "flex-start" }}>
        {copiedPlan && (
          <Grid size={{ xs: 12 }}>
            {/* How the end date of the copy is set */}
            <Dropdown
              label="Time duration"
              tag="required"
              value={durationMode}
              options={copyDurationModeOptions}
              onChange={(event) =>
                dispatch(setCopyDurationMode(event.target.value))
              }
              helpText={
                copiedPlan.remainingDates === null
                  ? "The copied plan has no time left to use"
                  : undefined
              }
              fullWidth
              containerSx={{ mt: 2 }}
              sx={{ height: "3.5rem", boxSizing: "border-box" }}
            />
          </Grid>
        )}

        <Grid size={{ xs: 6 }}>
          {/* Duration */}
          <TextField
            type="number"
            label={PLAN_DURATION_LABELS[durationType].amountField}
            error={validatePlanDuration({ durationType, durationValue })}
            // The dropdown next to it has a 4px gap between its label and its field, which a text
            // field does not, so the extra 4px keeps the two fields on the same line
            controlStyle={{ mt: 2.5 }}
            // Matches the height of the unit dropdown and the date fields next to it
            sx={{ height: "3.5rem", boxSizing: "border-box" }}
            text={isUnlimited ? "" : String(durationValue)}
            onTextChanged={(newDurationValue: string) => {
              dispatch(
                setCreationDates({
                  durationValue: Number.parseInt(newDurationValue, 10) || 0,
                })
              );
            }}
            tag="required"
            fullWidth
            disabled={isDurationFixed || isUnlimited}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          {/* Duration unit */}
          <Dropdown
            label="Unit"
            value={durationType}
            options={durationOptions}
            disabled={isDurationFixed}
            onChange={(event) => onDurationTypeChange(event.target.value)}
            fullWidth
            containerSx={{ mt: 2 }}
            sx={{ height: "3.5rem", boxSizing: "border-box" }}
          />
        </Grid>

        {!isUnlimited && (
          <>
            <Grid size={{ xs: 6 }}>
              {/* Start date */}
              <Stack sx={{ mt: 2, gap: 0.5 }}>
                <DatePickerLabel
                  label="Start Date"
                  isDisabled={isStartDateFixed}
                  infoTooltip={getCopyStartDateTooltip()}
                />
                <DateComponent
                  variant={datePickerVariant}
                  formSx={{ width: "100%", ...DATE_FIELD_ALIGNMENT_SX }}
                  date={new Date(startDate)}
                  onChange={(date) => {
                    // An unparseable date like 09/31 arrives as an Invalid Date, which is truthy
                    if (date && DateTimeUtils.isValidDate(date)) {
                      dispatch(setCreationDates({ startDate: date.getTime() }));
                    }
                  }}
                  // The copied plan ends when the copy starts, and a plan cannot be ended in the past
                  minDate={shouldEndCopiedPlan ? new Date() : undefined}
                  maxDate={maxStartDate}
                  disabled={isStartDateFixed}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 6 }}>
              {/* End date */}
              <Stack sx={{ mt: 2, gap: 0.5 }}>
                <DatePickerLabel
                  label="End Date"
                  isDisabled={copiedPlan !== null}
                  infoTooltip={
                    shouldDeriveDurationFromDates
                      ? undefined
                      : "The end date cannot be manually changed as it is calculated from the selected range and the start date"
                  }
                />
                <DateComponent
                  variant={datePickerVariant}
                  formSx={{ width: "100%", ...DATE_FIELD_ALIGNMENT_SX }}
                  date={new Date(endDate)}
                  onChange={(date) => {
                    if (date && DateTimeUtils.isValidDate(date)) {
                      dispatch(setPlanEndDate(date.getTime()));
                    }
                  }}
                  minDate={new Date(startDate)}
                  readOnly={!shouldDeriveDurationFromDates}
                  disabled={copiedPlan !== null}
                />
              </Stack>
            </Grid>
          </>
        )}
      </Grid>

      {!isUnlimited && (
        <PlanRangeInfo
          startDate={new Date(startDate)}
          endDate={new Date(endDate)}
        />
      )}

      {/* Ending the copied plan is only offered while the copy runs at the same time as it */}
      {copiedPlan && isCopyOverlapping && (
        <Switch
          label="Deactivate the current plan when the copy starts"
          checked={copiedPlan.shouldEndCopiedPlan}
          onToggle={() =>
            dispatch(setShouldEndCopiedPlan(!copiedPlan.shouldEndCopiedPlan))
          }
          sx={{ mt: 2, ml: 0 }}
        />
      )}
    </Grid>
  );
}
